<template>
  <div class="field has-addons">
    <p class="control is-expanded">
      <span
        class="input"
        :class="classes"
        readonly
        :disabled="!fieldDefinition.editable || undefined"
        v-on:click="openModal"
        >{{ modelDisplayString }}</span
      >
    </p>
    <p
      class="control"
      v-if="fieldDefinition.editable"
    >
      <button
        title="Select a model"
        class="button has-text-primary"
        type="button"
        v-on:click="openModal"
        :disabled="fieldData.loading || undefined"
      >
        <i class="icon-edit"></i>
      </button>
    </p>
    <p
      class="control"
      v-if="fieldDefinition.editable && fieldData.value !== null && !fieldDefinition.required"
    >
      <button
        class="button has-text-danger"
        type="button"
        v-on:click="removeModel"
        title="Deselect model"
        :disabled="fieldData.loading || undefined"
      >
        <i class="icon-minus"></i>
      </button>
    </p>
  </div>
  <p
    class="help is-danger"
    v-for="(error, i) in [...localErrors, ...fieldData.errors]"
    :key="i"
  >
    {{ error }}
  </p>
  <Modal
    v-model="pickerModal"
    title="Select a model"
  >
    <ModelPicker
      :compatible-worker-id="workerId"
      v-model="fieldData.value"
    />
  </Modal>
</template>

<script setup lang="ts">
import { isAxiosError } from 'axios'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import Modal from '@/components/Modal.vue'
import ModelPicker from '@/components/Model/ModelPicker.vue'
import { UUID_REGEX } from '@/config'
import { errorParser } from '@/helpers'
import { useModelStore } from '@/stores'

import { type FieldModel, type FieldProps, ValidationError, defineValidator } from '.'

type Constraint = { type: 'model'; many: false }
const { fieldDefinition, workerId } = defineProps<FieldProps<Constraint>>()
const fieldData = defineModel<FieldModel<Constraint>>({ required: true })

const classes = computed(() => ({
  'is-danger': !fieldData.value.valid,
  'is-loading': fieldData.value.loading,
  'is-clickable': fieldDefinition.editable || !!fieldData.value.value,
}))

const pickerModal = ref(false)

const router = useRouter()
const modelStore = useModelStore()

const modelDisplayString = computed<string>(() => {
  if (fieldData.value.value === null) return ''
  const model = modelStore.models[fieldData.value.value]
  if (!model) return fieldData.value.value
  return model.name
})

async function validator(id: unknown) {
  if (id === null) return
  if (typeof id !== 'string' || !UUID_REGEX.test(id))
    throw new ValidationError([`${id} is not a valid model ID`])

  // We already know about this model, it is valid
  if (id in modelStore.models) return

  try {
    await modelStore.retrieveModel(id)
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 404)
      throw new ValidationError(['This model does not exist or you do not have access to it'])
    throw new ValidationError([`Could not retrieve this model: ${errorParser(err)}`])
  }
}
const { localErrors } = defineValidator(fieldDefinition, fieldData.value, validator)

function openModal() {
  if (fieldData.value.loading) return
  // On an editable field, open the picker modal
  if (fieldDefinition.editable) pickerModal.value = true
  // On a non-editable field, open the model details in a new tab when a model is set
  else if (!fieldData.value.value) return
  else
    window.open(
      router.resolve({ name: 'model', params: { modelId: fieldData.value.value } }).href,
      '_blank',
    )
}

function removeModel() {
  if (!fieldData.value.loading) fieldData.value.value = null
}
</script>

<style lang="css" scoped>
.field + .help {
  margin-top: calc(-1 * var(--bulma-block-spacing));
}
</style>
